'use client';

import { motion } from 'framer-motion';
import Link from 'next/link';
import { TrendingUp, BarChart3, Zap } from 'lucide-react';

interface TradingHeroProps {
  backgroundComponent?: React.ReactNode;
}

export default function TradingHero({ backgroundComponent }: TradingHeroProps) {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const buttonVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    },
    hover: {
      scale: 1.05,
      boxShadow: "0 20px 40px rgba(255, 215, 0, 0.3)",
      transition: {
        duration: 0.2
      }
    },
    tap: {
      scale: 0.95
    }
  };

  return (
    <div className="relative w-full h-[500px] sm:h-[600px] lg:h-[700px] overflow-hidden group">
      {/* Enhanced Background with Overlay */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 filter blur-sm scale-110 transition-transform duration-700 group-hover:scale-105">
          {backgroundComponent || <div className="bg-gradient-to-br from-blue-900 via-purple-900 to-black" />}
        </div>
        {/* Animated Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
      </div>

      {/* Floating Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <motion.div
          className="absolute top-20 right-20 text-yellow-400/20"
          animate={{
            y: [-10, 10, -10],
            rotate: [0, 5, 0]
          }}
          transition={{
            duration: 4,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        >
          <TrendingUp size={60} />
        </motion.div>
        <motion.div
          className="absolute bottom-32 right-32 text-yellow-400/15"
          animate={{
            y: [10, -10, 10],
            rotate: [0, -5, 0]
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        >
          <BarChart3 size={40} />
        </motion.div>
      </div>

      {/* Enhanced Content Container */}
      <motion.div
        className="absolute inset-y-0 left-0 w-full md:w-2/3 lg:w-1/2 flex flex-col justify-center items-start p-6 sm:p-8 lg:p-12"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Glassmorphism Card */}
        <div className="backdrop-blur-md bg-white/5 border border-white/10 rounded-2xl p-6 sm:p-8 shadow-2xl hover:bg-white/10 transition-all duration-500">
          {/* Title with Enhanced Animation */}
          <motion.h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight"
            variants={itemVariants}
          >
            Bitcoin Fortress
            <span className="block bg-gradient-to-r from-yellow-400 via-yellow-500 to-orange-500 bg-clip-text text-transparent animate-pulse">
            INVESTMENTS AND TRADES
            </span>
          </motion.h2>

          {/* Enhanced Description */}
          <motion.p
            className="text-gray-300 text-base sm:text-lg mb-6 leading-relaxed max-w-lg"
            variants={itemVariants}
          >
            Stay updated with{' '}
            <span className="text-yellow-400 font-semibold">real-time forex cross rates</span>{' '}
            for major currency pairs, and place all crypto and forex trades on Bitcoin Fortress.
          </motion.p>

          {/* Feature Pills */}
          <motion.div
            className="flex flex-wrap gap-2 mb-6"
            variants={itemVariants}
          >
            {['Real-time Data', 'Secure Trading', 'Expert Analysis'].map((feature, index) => (
              <span
                key={feature}
                className="px-3 py-1 bg-yellow-400/20 text-yellow-300 text-sm rounded-full border border-yellow-400/30 backdrop-blur-sm"
              >
                {feature}
              </span>
            ))}
          </motion.div>

          {/* Enhanced Button */}
          <Link href='/dashboard'>
            <motion.button
              className="relative bg-gradient-to-r from-yellow-400 to-yellow-500 text-black font-bold py-3 px-8 rounded-xl overflow-hidden group/btn shadow-lg hover:shadow-yellow-400/25 transition-all duration-300"
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              {/* Button Shimmer Effect */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700" />
              
              {/* Button Content */}
              <span className="relative flex items-center gap-2">
                <Zap size={18} className="animate-pulse" />
                GET STARTED
              </span>
              
              {/* Glow Effect */}
              <div className="absolute inset-0 rounded-xl bg-yellow-400/20 blur-xl scale-150 opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300" />
            </motion.button>
          </Link>
        </div>
      </motion.div>

      {/* Animated Border */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute inset-0 border-2 border-transparent bg-gradient-to-r from-yellow-400/20 via-transparent to-yellow-400/20 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>
    </div>
  );
}